import React from "react";
import { Box } from "@mui/material";
import { useStore } from "utils/useStore";
import { ApplicationFiltersStore } from "stores/ApplicationFilters/ApplicationFiltersStore";
import AppliedFilterChipBase from "./AppliedFilterChipBase";
import { IAppliedFilterChipComponent } from "./IAppliedFilterChipComponent";

export const CHIP_AGGREGATION_THRESHOLD = 5;

const safetyEventModelNames: Record<string, string> = {
    "ml-adverseevents": "Adverse Event",
    "ml-adverseevents-p2": "Adverse Event",
    "ml-pqc": "Product Quality Complaint",
    "ml-specialsituation": "Special Situation",
    "ml-se-acknowledgement": "Safety Event Acknowledgement",
    "ml-se-identification": "Safety Event Identification",
};

export const chipModelName = (name: string): string =>
    safetyEventModelNames[name.toLowerCase()] ?? name;

export const buildHoverList = (items: string[]): React.ReactNode => (
    <Box component="ul" sx={{ pl: 1.5, my: 0 }}>
        {items.map((item, i) => (
            <li key={i}>{item}</li>
        ))}
    </Box>
);

export const sortAlphabetically = (items: string[]): string[] =>
    [...items].sort((a, b) => a.localeCompare(b));

export const chipItemLabel = (
    prefix: string,
    value: string,
    isExclusion: boolean,
): string =>
    isExclusion ? `${prefix}: NOT ${value}` : `${prefix}: ${value}`;

export const chipAggregatedLabel = (
    count: number,
    label: string,
    isExclusion: boolean,
): string =>
    isExclusion ? `Exclude ${count} ${label}` : `${count} ${label}`;

export const useChipFiltersStore = (props: IAppliedFilterChipComponent) => {
    const globalStore = useStore(ApplicationFiltersStore);
    return props.store ?? globalStore;
};

type RenderCollectionChipsProps<T> = Pick<
    IAppliedFilterChipComponent,
    "disabled" | "isApplicable"
> & {
    items: T[];
    getKey: (item: T) => React.Key;
    getLabel: (item: T) => React.ReactNode;
    getTooltipLabel: (item: T) => string;
    getAggregatedLabel: (count: number) => React.ReactNode;
    onRemoveItem: (item: T) => void;
    onRemoveAll: () => void;
};

export const renderCollectionChips = <T,>({
    items,
    disabled,
    isApplicable,
    getKey,
    getLabel,
    getTooltipLabel,
    getAggregatedLabel,
    onRemoveItem,
    onRemoveAll,
}: RenderCollectionChipsProps<T>): React.ReactNode => {
    if (items.length === 0) return null;

    if (items.length > CHIP_AGGREGATION_THRESHOLD) {
        const tooltipLabels = sortAlphabetically(items.map(getTooltipLabel)).filter(
            (label) => label.trim().length > 0,
        );

        return (
            <AppliedFilterChipBase
                disabled={disabled}
                isApplicable={isApplicable}
                onRemove={onRemoveAll}
                tooltipContent={
                    tooltipLabels.length > 0
                        ? buildHoverList(tooltipLabels)
                        : undefined
                }
            >
                {getAggregatedLabel(items.length)}
            </AppliedFilterChipBase>
        );
    }

    return items.map((item) => (
        <AppliedFilterChipBase
            key={getKey(item)}
            disabled={disabled}
            isApplicable={isApplicable}
            onRemove={() => onRemoveItem(item)}
        >
            {getLabel(item)}
        </AppliedFilterChipBase>
    ));
};
